import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { Textarea } from '@1trading/react/components/ui/textarea';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BINARY_ANSWERS } from '@/constants/answers';
import { hasAdverseRegulatoryHistory, type InstitutionRegulatoryInformationOrDefaults } from '@/features/institution/regulatory-information/schema';

const InstitutionRegulatoryInformationFieldsetHistory = () => {
  const form = useFormContext<InstitutionRegulatoryInformationOrDefaults>();
  const { t } = useTranslation();
  const [underInvestigation, individualsInvestigatedOrCensured, findingsAgainstFirmOrIndividuals, impendingProceedings] = form.watch([
    'underInvestigation',
    'individualsInvestigatedOrCensured',
    'findingsAgainstFirmOrIndividuals',
    'impendingProceedings',
  ]);
  const hasAdverseHistory = hasAdverseRegulatoryHistory({ underInvestigation, individualsInvestigatedOrCensured, findingsAgainstFirmOrIndividuals, impendingProceedings });

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="underInvestigation"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.regulatoryInformation.history.underInvestigation')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.regulatoryInformation.history.underInvestigation')}
                onValueChange={field.onChange}
              >
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="individualsInvestigatedOrCensured"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.regulatoryInformation.history.individualsInvestigatedOrCensured')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.regulatoryInformation.history.individualsInvestigatedOrCensured')}
                onValueChange={field.onChange}
              >
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="findingsAgainstFirmOrIndividuals"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.regulatoryInformation.history.findingsAgainstFirmOrIndividuals')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.regulatoryInformation.history.findingsAgainstFirmOrIndividuals')}
                onValueChange={field.onChange}
              >
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="impendingProceedings"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.regulatoryInformation.history.impendingProceedings')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.regulatoryInformation.history.impendingProceedings')}
                onValueChange={field.onChange}
              >
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {hasAdverseHistory ? (
        <FormField
          control={form.control}
          name="finesOrCensureDetails"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.regulatoryInformation.history.finesOrCensureDetails')}*</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder={t('institutionDetails.regulatoryInformation.placeholders.finesOrCensureDetails')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </div>
  );
};
InstitutionRegulatoryInformationFieldsetHistory.displayName = 'InstitutionRegulatoryInformationFieldsetHistory';

export { InstitutionRegulatoryInformationFieldsetHistory };
